// İSG uzmanı portalı — "Giriş" paneli (S32b `docs/reviews/S32b-gate-desk-relayout.md` §5, O3):
// QR VE T.C. Kimlik sorgusu TEK sekme grubunda, QR VARSAYILAN. Karar artık burada DEĞİL
// (`VerdictPanel.tsx`) — bu yüzden §2.1'deki "sekme çalınması" sorunu yoktur: sekme grubu
// YALNIZ giriş YÖNTEMİ seçer, uygulama HİÇBİR ZAMAN `setGirisTab` çağırmaz — TEK istisna
// aşağıdaki "forbidden default" etkisidir (kural 2, aşağıya bkz.), o da yalnız kullanıcı henüz
// hiçbir sekmeye dokunmamışken bir kerelik uygulanır.
//
// **Kapı oturumu sekimden BAĞIMSIZDIR** (kural 3) — `gate`/`gateSessionOpen` bu bileşene DIŞARIDAN
// gelir (`SecuritySiteSession`'da yaşar), sekme seçimi yoklamayı DURDURMAZ/BAŞLATMAZ.
//
// **Canlı işaret (kural 4).** Oturum açıkken VE `tc` seçiliyken `qr` sekme etiketi bir
// `bg-success` nokta + `sr-only` metin taşır (renk TEK BAŞINA DEĞİL) — aksi hâlde güvenlik kodun
// sekmenin ARKASINDA hâlâ çalıştığını bilemez. T.C. paneli de aynı süreçte kalıcı bir satır taşır.
//
// **Tab etiketleri — görünen KISA, erişilebilir ad TAM (§9.5 Almanca taşma fix'i).** Görünen
// metin `aria-hidden`, TAM cümle `sr-only` — ikisi AYNI ANDA görünmez/duyulmaz olduğundan
// (`aria-hidden` + `sr-only` çifti) görme engelli kullanıcı YALNIZ tam cümleyi duyar, gören
// kullanıcı YALNIZ kısa etiketi görür. `Tabs` PRİMİTİFİ DEĞİŞTİRİLMEDİ (altı başka tüketicisi
// var) — çağrı yerinde `label: ReactNode` zaten bunu destekliyordu.

import {
  type EntryVerifyResultDto,
  type TabItem,
  TabPanel,
  Tabs,
  type TcLookupResultDto,
} from '@isgportal/shared';
import { QrCode } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelHeader } from '../../components/PanelHeader';
import type { UseGateCodeResult } from '../../lib/useGateCode';
import { GateCodeTab } from './GateCodeTab';
import { ScanTab } from './ScanTab';
import { TcLookupTab } from './TcLookupTab';

// Üç giriş YÖNTEMİ (brief 092): `qr` = kapı QR'ı ÜRET (Akış B, varsayılan — owner O3), `scan` =
// personel QR'ını KAMERAYLA OKU (Akış A, `ScanTab`), `tc` = T.C. ile sorgu (Akış C).
type GirisTab = 'qr' | 'scan' | 'tc';

export interface EntryPanelProps {
  siteUuid: string;
  gate: UseGateCodeResult;
  gateSessionOpen: boolean;
  onStartSession: () => void;
  onStopSession: () => void;
  /** `security:gate-result` bilinen 403'ü (dayEntries'ten erken, `gate.panel.kind` beklemeden). */
  forbidden: boolean;
  onTcResult: (result: TcLookupResultDto) => void;
  onTcNetworkError: () => void;
  /** Akış A (`ScanTab`) — personel QR doğrulama sonucu / ağ hatası. Ağ hatası TC ile AYNI nötr
   *  "ağ hatası" kartına düşer (`SecuritySiteSession`'da ikisi de `pushTcError`/ayrı push). */
  onVerifyResult: (result: EntryVerifyResultDto) => void;
  onVerifyNetworkError: () => void;
  /** S32d owner talebi (`GateStage.tsx` dosya başı notu — TAM gerekçe orada) — sahnede bir karar
   *  varken kapı QR'ı `QR` sekmesinde GİZLENİR. */
  verdictPending: boolean;
}

export function EntryPanel({
  siteUuid,
  gate,
  gateSessionOpen,
  onStartSession,
  onStopSession,
  forbidden,
  onTcResult,
  onTcNetworkError,
  onVerifyResult,
  onVerifyNetworkError,
  verdictPending,
}: EntryPanelProps) {
  const { t } = useTranslation();
  const [girisTab, setGirisTab] = useState<GirisTab>('qr');
  // Kural 2 istisnası — forbidden BİLİNDİĞİNDE varsayılan `tc`'ye döner, ama YALNIZ kullanıcı
  // henüz sekmeye ELLE dokunmadıysa (bir sonraki `onValueChange` bu ref'i kalıcı olarak kapatır).
  // Bir `useEffect` içinde (render SIRASINDA `setState` + ref mutasyonu DEĞİL) — StrictMode'un
  // atılan ilk render'ı bir ref'i kalıcı olarak kirletmesin diye (React'ın belgelenen tuzağı).
  const userChangedTabRef = useRef(false);
  const forbiddenDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (forbidden && !forbiddenDefaultAppliedRef.current && !userChangedTabRef.current) {
      forbiddenDefaultAppliedRef.current = true;
      setGirisTab('tc');
    }
  }, [forbidden]);

  // Kapı kodu oturumu AÇIKKEN kullanıcı başka bir sekmedeyse (scan/tc), `qr` sekme etiketi canlı
  // bir işaret taşır — kod sekmenin ARKASINDA çalışmaya devam ediyor (kural 4, S32b §5).
  const liveMarkerActive = gateSessionOpen && girisTab !== 'qr';

  const tabs: TabItem[] = [
    {
      value: 'qr',
      label: (
        <span className="inline-flex items-center gap-1.5">
          {liveMarkerActive ? (
            <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
          ) : null}
          <span aria-hidden="true">{t('security.giris.tab.qr.short')}</span>
          <span className="sr-only">
            {liveMarkerActive
              ? `${t('security.tab.gate')} — ${t('security.giris.codeActiveSr')}`
              : t('security.tab.gate')}
          </span>
        </span>
      ),
    },
    {
      value: 'scan',
      label: (
        <span className="inline-flex items-center">
          <span aria-hidden="true">{t('security.giris.tab.scan.short')}</span>
          <span className="sr-only">{t('security.tab.scan')}</span>
        </span>
      ),
    },
    {
      value: 'tc',
      label: (
        <span className="inline-flex items-center">
          <span aria-hidden="true">{t('security.giris.tab.tc.short')}</span>
          <span className="sr-only">{t('security.tab.tc')}</span>
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col rounded-sm border border-border bg-surface shadow-sm">
      <PanelHeader
        icon={QrCode}
        title={t('security.panel.entry.title')}
        right={
          <Tabs
            items={tabs}
            value={girisTab}
            onValueChange={(v) => {
              userChangedTabRef.current = true;
              setGirisTab(v as GirisTab);
            }}
            ariaLabel={t('security.giris.tab.ariaLabel')}
            idPrefix="security-giris"
            className="border-b-0 pb-0"
          />
        }
      />
      <TabPanel value="qr" activeValue={girisTab} idPrefix="security-giris">
        <GateCodeTab
          sessionOpen={gateSessionOpen}
          gate={gate}
          onStart={onStartSession}
          onStop={onStopSession}
          forbidden={forbidden}
          verdictPending={verdictPending}
        />
      </TabPanel>
      <TabPanel value="scan" activeValue={girisTab} idPrefix="security-giris">
        {gateSessionOpen ? (
          <p className="mx-5 mt-3 rounded-md bg-surface-muted px-3 py-2 text-xs text-muted">
            {t('security.giris.codeStillLive')}
          </p>
        ) : null}
        <ScanTab onResult={onVerifyResult} onNetworkError={onVerifyNetworkError} />
      </TabPanel>
      <TabPanel value="tc" activeValue={girisTab} idPrefix="security-giris">
        {gateSessionOpen ? (
          <p className="mx-5 mt-3 rounded-md bg-surface-muted px-3 py-2 text-xs text-muted">
            {t('security.giris.codeStillLive')}
          </p>
        ) : null}
        <TcLookupTab siteUuid={siteUuid} onResult={onTcResult} onNetworkError={onTcNetworkError} />
      </TabPanel>
    </div>
  );
}
